import { useState } from 'react';
import type { Ctx } from '../ctx';
import { Modal } from '../ui';
import type { WebhookEntry } from '../types';

// The one destructive action in the console. The api refuses the last registration of ours outright; here the operator
// has to type the webhook id back before the button arms, and sees exactly which target is about to stop receiving.
export default function ConfirmDelete({ ctx, entry, onClose }: { ctx: Ctx; entry: WebhookEntry; onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const armed = typed.trim() === entry.id && !busy;

  const run = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await ctx.data.deleteWebhook(entry.id, typed.trim());
      ctx.toast(`Deleted ${entry.id} from ${entry.source}`);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Delete this ${entry.source} webhook?`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn danger" onClick={() => void run()} disabled={!armed}>
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </>
      }
    >
      <p className="note" style={{ margin: '0 0 8px' }}>
        {entry.source} stops sending <code>{entry.trigger}</code> to this target. It cannot be undone from here — the
        other system would have to register again.
      </p>
      <dl className="kv" style={{ marginBottom: 10 }}>
        <dt>Owner</dt>
        <dd>{entry.owner === 'ours' ? 'ours (superseded by a newer registration)' : entry.owner === 'zapier' ? 'Zapier' : 'another service'}</dd>
        <dt>Target</dt>
        <dd className="wrap">
          <code>{entry.target}</code>
        </dd>
        <dt>Id</dt>
        <dd>
          <code>{entry.id}</code>
        </dd>
      </dl>
      {entry.note ? (
        <div className="notice" style={{ marginBottom: 10 }}>
          <strong>{entry.note}</strong>
        </div>
      ) : null}
      <label>
        Type the webhook id to confirm
        <br />
        <input
          type="text"
          aria-label="confirm webhook id"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={entry.id}
          style={{ width: '100%', fontFamily: 'monospace' }}
          autoFocus
        />
      </label>
      {err ? (
        <div className="notice" style={{ marginTop: 10 }}>
          <code>{err}</code>
        </div>
      ) : null}
    </Modal>
  );
}
